import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card, PageHeader, Empty } from "@/components/common";
import { toast } from "sonner";
import { Save, Plus, Trash2, Sliders, LayoutGrid, MapPin, History, Stethoscope } from "lucide-react";

const THRESH_FIELDS = [
  ["dosing_error_tolerance", "Dosing Error Tolerance (default 0)"],
  ["attendance_flag_uninformed", "Uninformed Absence Flag (rolling 30d)"],
  ["attendance_bonus_min_pct", "Attendance Bonus Minimum %"],
  ["incentive_per_procedure", "Per-Procedure Bonus (₹)"],
  ["incentive_zero_dosing_bonus", "Zero Dosing-Error Bonus (₹)"],
  ["incentive_result_quality_bonus", "Result-Quality Bonus / Excellent (₹)"],
  ["incentive_attendance_bonus", "Punctuality / Attendance Bonus (₹)"],
  ["incentive_upsell_pct", "Cross-sell / Upsell Bonus (% of value)"],
  ["incentive_review_rate", "Review Rate (₹ per review)"],
  ["incentive_testimonial_rate", "Testimonial Rate (₹ per video)"],
];

export default function Settings() {
  const { branches, loadReference } = useApp();
  const [thresholds, setThresholds] = useState({});
  const [pillars, setPillars] = useState([]);
  const [branchList, setBranchList] = useState([]);
  const [newBranch, setNewBranch] = useState("");
  const [log, setLog] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [newProc, setNewProc] = useState("");
  const inp = "bg-[#0f172a] border border-white/10 rounded-md px-3 py-2 text-sm w-full focus:ring-2 focus:ring-[#C9A24B] focus:outline-none";

  useEffect(() => {
    api.get("/settings/thresholds").then(({ data }) => setThresholds(data));
    api.get("/settings/pillars").then(({ data }) => setPillars(data));
    api.get("/settings/branches").then(({ data }) => setBranchList(data));
    api.get("/settings/changelog").then(({ data }) => setLog(data));
    api.get("/procedures").then(({ data }) => setProcedures(data));
  }, []);

  const saveThresholds = async () => { await api.put("/settings/thresholds", thresholds); toast.success("Thresholds & incentive slabs saved"); };
  const savePillars = async () => { await api.put("/settings/pillars", pillars); toast.success("Pillars saved"); };
  const saveBranches = async () => { await api.put("/settings/branches", branchList); toast.success("Branches saved"); loadReference(); };
  const reloadProcs = () => api.get("/procedures").then(({ data }) => setProcedures(data));
  const addProcedure = async () => { if (!newProc.trim()) return; try { await api.post("/procedures", { name: newProc.trim(), active: true }); setNewProc(""); reloadProcs(); toast.success("Procedure added"); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const saveProcedure = async (p) => { await api.put(`/procedures/${p.id}`, { name: p.name, active: p.active }); reloadProcs(); toast.success("Procedure name saved"); };
  const toggleProcedure = async (p) => { await api.put(`/procedures/${p.id}`, { name: p.name, active: !p.active }); reloadProcs(); toast.success(!p.active ? "Procedure activated" : "Procedure deactivated — history kept"); };

  return (
    <div className="animate-fade-up" data-testid="settings-page">
      <PageHeader title="Settings" subtitle="Configure pillars, thresholds, incentive slabs and branches without a code change." />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-5">
          <h3 className="font-heading text-lg mb-4 flex items-center gap-2"><Sliders size={18} className="text-[#C9A24B]" /> Thresholds & Incentive Slabs</h3>
          <div className="space-y-3">
            {THRESH_FIELDS.map(([k, label]) => (
              <div key={k} className="flex items-center justify-between gap-3">
                <label className="text-sm text-slate-300">{label}</label>
                <input data-testid={`thresh-${k}`} type="number" className={inp + " max-w-[120px]"} value={thresholds[k] ?? ""} onChange={(e) => setThresholds({ ...thresholds, [k]: parseFloat(e.target.value || 0) })} />
              </div>
            ))}
          </div>
          <button data-testid="save-thresholds-btn" onClick={saveThresholds} className="mt-4 flex items-center gap-2 bg-emerald-600 text-white font-semibold px-4 py-2 rounded-md hover:brightness-110 transition-all"><Save size={16} /> Save</button>
        </Card>

        <div className="space-y-6">
          <Card className="p-5">
            <h3 className="font-heading text-lg mb-4 flex items-center gap-2"><LayoutGrid size={18} className="text-[#C9A24B]" /> 7 Pillars</h3>
            <div className="space-y-2">
              {pillars.map((p, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input className={inp} value={p.name} onChange={(e) => { const c = [...pillars]; c[i] = { ...c[i], name: e.target.value }; setPillars(c); }} />
                  <input type="number" className={inp + " max-w-[80px]"} value={p.weight} onChange={(e) => { const c = [...pillars]; c[i] = { ...c[i], weight: parseInt(e.target.value || 0) }; setPillars(c); }} />
                  <button onClick={() => setPillars(pillars.filter((_, j) => j !== i))} className="text-slate-400 hover:text-[#DC2626]"><Trash2 size={16} /></button>
                </div>
              ))}
              <button onClick={() => setPillars([...pillars, { id: `custom_${Date.now()}`, name: "New Pillar", weight: 10 }])} className="text-xs text-[#C9A24B] flex items-center gap-1"><Plus size={14} /> Add Pillar</button>
            </div>
            <button data-testid="save-pillars-btn" onClick={savePillars} className="mt-4 flex items-center gap-2 bg-emerald-600 text-white font-semibold px-4 py-2 rounded-md hover:brightness-110 transition-all"><Save size={16} /> Save</button>
          </Card>

          <Card className="p-5">
            <h3 className="font-heading text-lg mb-4 flex items-center gap-2"><MapPin size={18} className="text-[#C9A24B]" /> Branch Management</h3>
            <div className="space-y-2">
              {branchList.map((b, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input className={inp} value={b} onChange={(e) => { const c = [...branchList]; c[i] = e.target.value; setBranchList(c); }} />
                  <button onClick={() => setBranchList(branchList.filter((_, j) => j !== i))} className="text-slate-400 hover:text-[#DC2626]"><Trash2 size={16} /></button>
                </div>
              ))}
              <div className="flex items-center gap-2">
                <input data-testid="new-branch-input" className={inp} placeholder="New branch name" value={newBranch} onChange={(e) => setNewBranch(e.target.value)} />
                <button onClick={() => { if (newBranch) { setBranchList([...branchList, newBranch]); setNewBranch(""); } }} className="bg-white/10 px-3 py-2 rounded-md"><Plus size={16} /></button>
              </div>
            </div>
            <button data-testid="save-branches-btn" onClick={saveBranches} className="mt-4 flex items-center gap-2 bg-emerald-600 text-white font-semibold px-4 py-2 rounded-md hover:brightness-110 transition-all"><Save size={16} /> Save</button>
          </Card>
        </div>
      </div>

      <Card className="p-5 mt-6" data-testid="procedure-master-card">
        <h3 className="font-heading text-lg mb-1 flex items-center gap-2"><Stethoscope size={18} className="text-[#C9A24B]" /> Procedure Master</h3>
        <p className="text-xs text-slate-500 mb-4">Add, rename or deactivate procedures. These feed the Surgery Log dropdown automatically — no code change needed. Deactivating keeps historical surgery records intact.</p>
        <div className="flex items-center gap-2 mb-4 max-w-md">
          <input data-testid="new-procedure-input" className={inp} placeholder="New procedure name (e.g. Breast Augmentation)" value={newProc} onChange={(e) => setNewProc(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addProcedure()} />
          <button data-testid="add-procedure-btn" onClick={addProcedure} className="flex items-center gap-1 bg-[#C9A24B] text-[#0b1120] font-semibold px-3 py-2 rounded-md hover:brightness-110 transition-all whitespace-nowrap"><Plus size={16} /> Add</button>
        </div>
        <div className="grid sm:grid-cols-2 gap-2">
          {procedures.map((p, i) => (
            <div key={p.id} className={`flex items-center gap-2 rounded-md px-2 py-1.5 bg-white/5 ${p.active ? "" : "opacity-60"}`} data-testid={`procedure-row-${p.id}`}>
              <input className={inp} value={p.name} onChange={(e) => { const c = [...procedures]; c[i] = { ...c[i], name: e.target.value }; setProcedures(c); }} />
              <button onClick={() => saveProcedure(p)} className="text-slate-400 hover:text-emerald-400" title="Save name"><Save size={16} /></button>
              <button data-testid={`toggle-procedure-${p.id}`} onClick={() => toggleProcedure(p)} className={`text-xs px-2 py-1 rounded whitespace-nowrap font-medium ${p.active ? "text-emerald-400 hover:text-[#DC2626]" : "text-slate-400 hover:text-emerald-400"}`}>{p.active ? "Active" : "Inactive"}</button>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-5 mt-6">
        <h3 className="font-heading text-lg mb-4 flex items-center gap-2"><History size={18} className="text-[#C9A24B]" /> Change Log</h3>
        {log.length === 0 ? <Empty text="No changes recorded yet." /> : (
          <div className="max-h-72 overflow-y-auto space-y-1 text-sm">
            {log.map((l) => (
              <div key={l.id} className="flex items-center gap-3 py-1.5 border-b border-white/5" data-testid={`log-${l.id}`}>
                <span className="text-[10px] uppercase px-2 py-0.5 rounded bg-white/10 text-slate-300">{l.action}</span>
                <span className="text-slate-300">{l.entity}</span>
                <span className="text-slate-500 text-xs flex-1">{l.detail}</span>
                <span className="text-slate-500 text-xs">{l.user_name}</span>
                <span className="text-slate-600 text-xs">{(l.timestamp || "").slice(0, 16).replace("T", " ")}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
